import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import { 
  Download, 
  Search, 
  ShieldCheck, 
  FolderArchive, 
  Sparkles, 
  ExternalLink, 
  Layers, 
  FileCheck, 
  Filter, 
  RefreshCw,
  HardDrive
} from 'lucide-react';

import { fetchLibrary, requestDownload } from '../api/client';

export default function Library() {
  const { user, session } = useAuth();
  const navigate = useNavigate();
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');

  // Fallback initial items for demonstration
  const sampleLibrary = [
    {
      id: 'd-1',
      title: 'Glassmorphism UI Kit Pro',
      designer: 'Kwame Adu',
      slug: 'glassmorphism-ui-kit-pro',
      license: 'Standard Commercial',
      format: 'FIG',
      size: '142 MB',
      version: 'v2.1',
      hasUpdate: true,
      downloaded_at: new Date().toISOString(),
      preview_url: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=600&q=80'
    },
    {
      id: 'd-2',
      title: 'Cyberpunk 3D Hologram Pack',
      designer: 'Sarah Lin',
      slug: 'cyberpunk-3d-hologram-pack',
      license: 'Extended Commercial',
      format: 'BLEND',
      size: '480 MB',
      version: 'v1.0',
      hasUpdate: false,
      downloaded_at: new Date(Date.now() - 86400000 * 3).toISOString(),
      preview_url: 'https://images.unsplash.com/photo-1634017839464-5c339ebe3cb4?w=600&q=80'
    },
    {
      id: 'd-3',
      title: 'Minimalist Branding Mockups Vol. 2',
      designer: 'Alex Rivera',
      slug: 'minimalist-branding-mockups-vol-2',
      license: 'Personal License',
      format: 'PSD',
      size: '320 MB',
      version: 'v1.4',
      hasUpdate: false,
      downloaded_at: new Date(Date.now() - 86400000 * 7).toISOString(),
      preview_url: 'https://images.unsplash.com/photo-1600132806370-bf17e65e942f?w=600&q=80'
    }
  ];

  useEffect(() => {
    if (!user) {
      setDownloads(sampleLibrary);
      return;
    }
    
    setLoading(true);
    fetchLibrary()
      .then(data => {
        if (data.library && data.library.length > 0) {
          setDownloads(data.library);
        } else {
          setDownloads(sampleLibrary);
        }
      })
      .catch(() => {
        setDownloads(sampleLibrary);
      })
      .finally(() => setLoading(false));
  }, [user, session]);

  const handleRedownload = async (templateId) => {
    try {
      const data = await requestDownload(templateId);
      if (data?.downloadUrl) {
        window.open(data.downloadUrl, '_blank');
        return;
      }
      alert('Asset package is being prepared for secure download...');
    } catch (err) {
      alert(err.message || 'Download request error');
    }
  };

  const filteredItems = downloads.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) || item.designer.toLowerCase().includes(searchQuery.toLowerCase());
    if (filterType === 'commercial') return matchesSearch && item.license?.includes('Commercial');
    if (filterType === 'updates') return matchesSearch && item.hasUpdate;
    return matchesSearch;
  });

  return (
    <div className="page-container" style={{ paddingBottom: '100px', paddingTop: '32px' }}>
      
      {/* Vault Header & Storage Stats */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '24px', marginBottom: '36px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
            <h1 className="text-h1">Personal Asset Vault</h1>
            <span style={{
              background: 'var(--primary-subtle)',
              color: 'var(--primary-text)',
              fontSize: '0.75rem',
              fontWeight: 700,
              padding: '3px 10px',
              borderRadius: '999px',
              border: '1px solid var(--primary-border)'
            }}>
              {downloads.length} Assets Stored
            </span>
          </div>
          <p className="text-secondary" style={{ fontSize: '0.9375rem' }}>
            Access all your purchased and collected creative resources with lifetime re-download rights.
          </p>
        </div>

        {/* Storage Bar Card */}
        <div style={{
          background: 'var(--bg-surface)',
          padding: '16px 20px',
          borderRadius: '16px',
          border: '1px solid var(--border-subtle)',
          minWidth: '260px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem', marginBottom: '6px' }}>
            <span style={{ color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <HardDrive size={14} color="var(--primary)" /> Total Cloud Archive
            </span>
            <strong style={{ color: 'var(--text-primary)' }}>942 MB</strong>
          </div>
          <div style={{ height: '6px', background: 'var(--bg-base)', borderRadius: '999px', overflow: 'hidden' }}>
            <div style={{ width: '42%', height: '100%', background: 'linear-gradient(90deg, var(--primary), var(--accent))' }} />
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px',
        marginBottom: '28px'
      }}>
        {/* Search */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-subtle)',
          borderRadius: '12px',
          padding: '8px 14px',
          flex: 1,
          maxWidth: '380px',
          gap: '8px'
        }}>
          <Search size={16} color="var(--text-tertiary)" />
          <input
            type="text"
            placeholder="Search your library..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-primary)',
              outline: 'none',
              fontSize: '0.875rem',
              width: '100%'
            }}
          />
        </div>

        {/* Filter Pills */}
        <div style={{ display: 'flex', gap: '8px' }}>
          {[
            { id: 'all', label: 'All Assets' },
            { id: 'commercial', label: 'Commercial Licenses' },
            { id: 'updates', label: 'Updates Available' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setFilterType(tab.id)}
              style={{
                padding: '8px 14px',
                borderRadius: '10px',
                fontSize: '0.8125rem',
                fontWeight: 600,
                background: filterType === tab.id ? 'var(--bg-surface-active)' : 'var(--bg-surface)',
                color: filterType === tab.id ? 'var(--text-primary)' : 'var(--text-secondary)',
                border: `1px solid ${filterType === tab.id ? 'var(--border-strong)' : 'var(--border-subtle)'}`,
                cursor: 'pointer'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Grid of Vault Assets */}
      {filteredItems.length === 0 ? (
        <div style={{ padding: '80px 20px', backgroundColor: 'var(--bg-surface)', borderRadius: '24px', textAlign: 'center', border: '1px solid var(--border-subtle)' }}>
          <FolderArchive size={48} color="var(--text-tertiary)" style={{ margin: '0 auto 16px' }} />
          <h3 className="text-h3" style={{ marginBottom: '8px' }}>No matching assets</h3>
          <p className="text-secondary" style={{ marginBottom: '24px', maxWidth: '400px', margin: '0 auto 24px' }}>
            Explore thousands of professional design systems and 3D assets on the marketplace.
          </p>
          <Button variant="primary" onClick={() => navigate('/browse')}>Browse Marketplace</Button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '24px' }}>
          {filteredItems.map((item) => (
            <div
              key={item.id}
              className="interactive-card"
              style={{
                backgroundColor: 'var(--bg-surface)',
                borderRadius: '20px',
                overflow: 'hidden',
                border: '1px solid var(--border-subtle)',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {/* Image Preview with Format & Version Badges */}
              <div style={{ aspectRatio: '16/10', overflow: 'hidden', position: 'relative' }}>
                <img
                  src={item.preview_url}
                  alt={item.title}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                
                <div style={{
                  position: 'absolute',
                  top: '12px',
                  left: '12px',
                  display: 'flex',
                  gap: '6px'
                }}>
                  <span style={{ background: 'rgba(9,9,16,0.8)', backdropFilter: 'blur(8px)', padding: '3px 8px', borderRadius: '6px', fontSize: '0.6875rem', fontWeight: 800, color: '#FFFFFF' }}>
                    {item.format || 'FIG'}
                  </span>
                  <span style={{ background: 'rgba(9,9,16,0.8)', backdropFilter: 'blur(8px)', padding: '3px 8px', borderRadius: '6px', fontSize: '0.6875rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
                    {item.version || 'v1.0'}
                  </span>
                </div>

                {item.hasUpdate && (
                  <div style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: 'var(--accent)',
                    color: '#000000',
                    fontSize: '0.6875rem',
                    fontWeight: 800,
                    padding: '3px 8px',
                    borderRadius: '999px',
                    boxShadow: '0 2px 8px rgba(59, 246, 134, 0.4)'
                  }}>
                    UPDATE AVAILABLE
                  </div>
                )}
              </div>

              {/* Asset Info Body */}
              <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                <Link to={`/template/${item.slug || item.id}`} style={{ textDecoration: 'none', color: 'var(--text-primary)', marginBottom: '4px' }}>
                  <h3 style={{ fontSize: '1.05rem', fontWeight: 800, lineHeight: 1.3 }}>{item.title}</h3>
                </Link>
                
                <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: '14px' }}>
                  By <strong>{item.designer}</strong>
                </div>

                {/* License Tag & Size */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: 'var(--bg-base)', borderRadius: '10px', border: '1px solid var(--border-subtle)', marginBottom: '16px', fontSize: '0.75rem' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--accent)', fontWeight: 600 }}>
                    <ShieldCheck size={13} /> {item.license || 'Commercial License'}
                  </span>
                  <span style={{ color: 'var(--text-tertiary)' }}>{item.size || '120 MB'}</span>
                </div>

                {/* Footer Action */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto', paddingTop: '12px', borderTop: '1px solid var(--border-subtle)' }}>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                    Saved {new Date(item.downloaded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                  <Button variant="secondary" onClick={() => handleRedownload(item.id)}>
                    <Download size={14} /> Re-download
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
